import { signInWithEmailAndPassword } from "firebase/auth";
import {
  clearFirebaseLocalStorage,
  initFirebase,
} from "@core/services/api/firebase";
import { apiRequest } from "@core/services/api/apiRequest";

type Args = {
  data: any;
  onError?: (error: Error) => void;
  /**
   * Where the Firebase token is sent: the login endpoint unless something
   * else needs proof of who somebody is, as Welcome Back's check-in does
   */
  apiEndpoint?: string;
  /** Sent with the token */
  extraBody?: Record<string, unknown>;
};

export async function loginUser({ data, onError, apiEndpoint = "/auth/login/", extraBody }: Args) {

  try {
    // Prevalidate: clean up orphans, check if account exists in DB.
    // Prevents confusing errors when Firebase auth succeeds but backend
    // returns "user not found".
    try {
      const { exists, retry_after } = await apiRequest(
        "/auth/prevalidate/",
        "POST",
        { email: data.email },
        false
      );
      if (!exists && retry_after) {
        throw new Error("Please wait a few minutes and try again.");
      }
      if (!exists) {
        throw new Error("No account found. Please sign up first.");
      }
    } catch (prevalidateError) {
      // If no account exists, surface that error immediately.
      // For any other prevalidate failure, proceed anyway (graceful degradation).
      if (
        (prevalidateError as Error).message?.includes("No account found") ||
        (prevalidateError as Error).message?.includes("Please wait")
      ) {
        throw prevalidateError;
      }
    }

    // Ensure firebase is initialized
    const auth = await initFirebase();

    // Login user
    const userCredential = await signInWithEmailAndPassword(
      auth,
      data.email,
      data.password
    );

    // Get user
    const user = userCredential.user;

    // Get ID token
    const idToken = await user.getIdToken();

    // Prepare data for API call
    const body = { ...extraBody, firebase_token: idToken };

    // Send data to backend (requireAuth=false since user is logging in)
    const response = await apiRequest(apiEndpoint, "POST", body, false);

    return { authToken: idToken, ...response };
  } catch (error) {
    clearFirebaseLocalStorage();
    onError?.(error as Error);

    throw error;
  }
}

export default loginUser;
